import { Position } from '../stores/gameStore'

const R = 4
const T = 4

function isHex(q: number, r: number): boolean {
  const s = -q - r
  return Math.abs(q) <= R && Math.abs(r) <= R && Math.abs(s) <= R
}

function getTriangleRegion(q: number, r: number): 'player1' | 'player2' | 'zone3' | 'zone4' | 'zone5' | 'zone6' | null {
  const s = -q - r
  
  // Tapering constraints: "Side" coords must be within [-R, R]
  
  // Triangle 1 (Bottom-Right, Zone 4): q > R
  if (q > R && r < 0 && s < 0 && r >= -R && s >= -R) return 'zone4'
  
  // Triangle 2 (Bottom, Player 1): r > R
  if (r > R && q < 0 && s < 0 && q >= -R && s >= -R) return 'player1'
  
  // Triangle 3 (Bottom-Left, Zone 6): s > R
  if (s > R && q < 0 && r < 0 && q >= -R && r >= -R) return 'zone6'
  
  // Triangle 4 (Top-Left, Zone 5): q < -R
  if (q < -R && r > 0 && s > 0 && r <= R && s <= R) return 'zone5'
  
  // Triangle 5 (Top, Player 2): r < -R
  if (r < -R && q > 0 && s > 0 && q <= R && s <= R) return 'player2'
  
  // Triangle 6 (Top-Right, Zone 3): s < -R
  if (s < -R && q > 0 && r > 0 && q <= R && r <= R) return 'zone3'
  
  return null
}

// Generate all valid positions for the Chinese Checkers star board
export function generateBoardPositions(): Position[] {
  const positions: Position[] = []
  
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (isHex(q, r) || getTriangleRegion(q, r) !== null) {
        positions.push({ q, r })
      }
    }
  }

  return positions
}

// Define home zones for all 6 triangles
export function getPlayer1Home(): Set<string> {
  const home = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'player1') {
        home.add(`${q},${r}`)
      }
    }
  }
  return home
}

export function getPlayer2Home(): Set<string> {
  const home = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'player2') {
        home.add(`${q},${r}`)
      }
    }
  }
  return home
}

// Additional zones for visual consistency (6-player positions)
export function getZone3(): Set<string> {
  const zone = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'zone3') zone.add(`${q},${r}`)
    }
  }
  return zone
}

export function getZone4(): Set<string> {
  const zone = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'zone4') zone.add(`${q},${r}`)
    }
  }
  return zone
}

export function getZone5(): Set<string> {
  const zone = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'zone5') zone.add(`${q},${r}`)
    }
  }
  return zone
}

export function getZone6(): Set<string> {
  const zone = new Set<string>()
  for (let q = -R - T; q <= R + T; q++) {
    for (let r = -R - T; r <= R + T; r++) {
      if (getTriangleRegion(q, r) === 'zone6') zone.add(`${q},${r}`)
    }
  }
  return zone
}

// Convert axial coordinates to pixel position for rendering
export function axialToPixel(q: number, r: number, size: number): { x: number; y: number } {
  // Standard axial mapping
  const rawX = size * (3 / 2 * q)
  const rawY = size * (Math.sqrt(3) / 2 * q + Math.sqrt(3) * r)
  
  // Rotate by -30 degrees
  const angle = -Math.PI / 6
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  
  const x = rawX * cos - rawY * sin
  const y = rawX * sin + rawY * cos
  
  return { x, y }
}

// Get the bounding box for the board
export function getBoardBounds(size: number): { minX: number; maxX: number; minY: number; maxY: number } {
  const positions = generateBoardPositions()
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity

  for (const pos of positions) {
    const { x, y } = axialToPixel(pos.q, pos.r, size)
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }

  return { minX, maxX, minY, maxY }
}

// Check if a position is in player 1's starting zone (bottom)
export function isPlayer1Zone(pos: Position): boolean {
  return getPlayer1Home().has(`${pos.q},${pos.r}`)
}

// Check if a position is in player 2's starting zone (top)
export function isPlayer2Zone(pos: Position): boolean {
  return getPlayer2Home().has(`${pos.q},${pos.r}`)
}

// Get zone color for a position (all 6 triangles for visual appeal)
export function getZoneColor(pos: Position): 'player1' | 'player2' | 'zone3' | 'zone4' | 'zone5' | 'zone6' | null {
  if (isPlayer1Zone(pos)) return 'player1'
  if (isPlayer2Zone(pos)) return 'player2'

  const key = `${pos.q},${pos.r}`
  if (getZone3().has(key)) return 'zone3'
  if (getZone4().has(key)) return 'zone4'
  if (getZone5().has(key)) return 'zone5'
  if (getZone6().has(key)) return 'zone6'

  return null
}

// Get all neighbors for a position (for drawing connections)
export function getNeighbors(pos: Position): Position[] {
  const directions = [
    { q: 1, r: 0 },   // East
    { q: -1, r: 0 },  // West
    { q: 0, r: 1 },   // SE
    { q: 0, r: -1 },  // NW
    { q: 1, r: -1 },  // NE
    { q: -1, r: 1 },  // SW
  ]
  return directions.map(d => ({ q: pos.q + d.q, r: pos.r + d.r }))
}
